import { canvasFlatten, canvasThreshold, createCanvas } from './canvas.utils'

/**
 * StarPRNT Language Commands
 */
export class StarPrntEncoder {
  private buffer: (number | Uint8Array)[] = []

  constructor() {
    this.reset()
  }

  /**
   * Reset the state of the object
   */
  private reset(): void {
    this.buffer = []
  }

  /**
   * Add commands to the buffer
   * @param {array} value Array of numbers, arrays, buffers or Uint8Array to add to the buffer
   */
  private queue(value: (number | Uint8Array)[]): void {
    value.forEach(item => this.buffer.push(item))
  }

  /**
   * Initialize the printer
   * @return {object} Return the object, for easy chaining commands
   */
  initialize(): this {
    this.queue([0x1b, 0x40, 0x18]) // ESC @ CAN - Initialize printer
    return this
  }

  /**
   * Change text alignment
   * @param {string} value left, center or right
   * @return {object} Return the object, for easy chaining commands
   */
  align(value: 'left' | 'center' | 'right'): this {
    let align = 0x00

    if (value === 'center') {
      align = 0x01
    } else if (value === 'right') {
      align = 0x02
    }

    this.queue([0x1b, 0x1d, 0x61, align]) // ESC GS a n - Set alignment
    return this
  }

  /**
   * Print a newline (paper feed without printing)
   * @param {number} count Number of line feeds to print (default: 1)
   * @return {object} Return the object, for easy chaining commands
   */
  newline(count: number = 1): this {
    for (let i = 0; i < count; i++) {
      this.queue([0x1b, 0x61, 0x01]) // ESC a SOH - Paper feed
    }
    return this
  }

  /**
   * Image encoding
   * @param {CanvasImageSource} element An element, like a canvas or image that needs to be printed
   * @param {number} width Width of the image
   * @param {number} height Height of the image
   * @return {object} Return the object, for easy chaining commands
   */
  image(element: CanvasImageSource, width: number, height: number): this {
    if (width % 8 !== 0) {
      throw new Error('Width must be a multiple of 8')
    }
    if (height % 8 !== 0) {
      throw new Error('Height must be a multiple of 8')
    }

    const canvas = createCanvas(width, height)
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not get 2d context from canvas')

    context.drawImage(element, 0, 0, width, height)
    let image = context.getImageData(0, 0, width, height)
    image = canvasFlatten(image, [0xff, 0xff, 0xff])
    image = canvasThreshold(image, 128)

    const getPixel = (x: number, y: number) =>
      typeof image.data[(width * y + x) * 4] === 'undefined' ||
      image.data[(width * y + x) * 4] > 0
        ? 0
        : 1

    this.queue([0x1b, 0x30]) // ESC 0 - Select 24-pin graphics mode

    for (let s = 0; s < height / 24; s++) {
      const y = s * 24
      const bytes = new Uint8Array(width * 3)

      for (let x = 0; x < width; x++) {
        const i = x * 3

        bytes[i] =
          (getPixel(x, y + 0) << 7) |
          (getPixel(x, y + 1) << 6) |
          (getPixel(x, y + 2) << 5) |
          (getPixel(x, y + 3) << 4) |
          (getPixel(x, y + 4) << 3) |
          (getPixel(x, y + 5) << 2) |
          (getPixel(x, y + 6) << 1) |
          getPixel(x, y + 7)

        bytes[i + 1] =
          (getPixel(x, y + 8) << 7) |
          (getPixel(x, y + 9) << 6) |
          (getPixel(x, y + 10) << 5) |
          (getPixel(x, y + 11) << 4) |
          (getPixel(x, y + 12) << 3) |
          (getPixel(x, y + 13) << 2) |
          (getPixel(x, y + 14) << 1) |
          getPixel(x, y + 15)

        bytes[i + 2] =
          (getPixel(x, y + 16) << 7) |
          (getPixel(x, y + 17) << 6) |
          (getPixel(x, y + 18) << 5) |
          (getPixel(x, y + 19) << 4) |
          (getPixel(x, y + 20) << 3) |
          (getPixel(x, y + 21) << 2) |
          (getPixel(x, y + 22) << 1) |
          getPixel(x, y + 23)
      }

      this.queue([
        0x1b, // ESC
        0x58, // X - Graphics data
        width & 0xff, // Width low byte
        (width >> 8) & 0xff, // Width high byte
        ...bytes, // Image data
        0x0a, // LF - Line feed
        0x0d // CR - Carriage return
      ])
    }

    this.queue([0x1b, 0x7a, 0x01]) // ESC z SOH - End graphics mode

    return this
  }

  /**
   * Cut paper
   * @param {string} value full or partial. When not specified a full cut will be assumed
   * @return {object} Return the object, for easy chaining commands
   */
  cut(value?: 'full' | 'partial'): this {
    let data = 0x02 // Paper is fed to cutting position, then a full cut.

    if (value === 'partial') {
      data = 0x03 // Paper is fed to cutting position, then a partial cut.
    }

    this.queue([0x1b, 0x64, data]) // ESC d n - Cut paper
    return this
  }

  /**
   * Send a pulse to the cash drawer
   * @return {object} Return the object, for easy chaining commands
   */
  openCashDrawer(): this {
    const on = Math.min(127, Math.round(200 / 10)) //  200ms ON time
    const off = Math.min(127, Math.round(200 / 10)) //  200ms OFF time

    this.queue([0x1b, 0x07, on & 0xff, 0x00, 0x07]) // ESC BEL on_time 0 pin - ON pulse
    this.queue([0x1b, 0x07, 0x00, off & 0xff, 0x07]) // ESC BEL 0 off_time pin - OFF pulse
    return this
  }

  /**
   * Add raw printer commands
   * @param {Array} data Raw bytes to be included
   * @return {object} Return the object, for easy chaining commands
   */
  raw(data: (number | Uint8Array)[]): this {
    this.queue(data)
    return this
  }

  /**
   * Encode all previous commands
   * @return {Uint8Array} Return the encoded bytes
   */
  encode(): Uint8Array {
    let length = 0
    this.buffer.forEach(item => {
      if (typeof item === 'number') {
        length++
      } else {
        length += item.length
      }
    })

    const result = new Uint8Array(length)
    let index = 0
    this.buffer.forEach(item => {
      if (typeof item === 'number') {
        result[index] = item
        index++
      } else {
        result.set(item, index)
        index += item.length
      }
    })

    this.reset()
    return result
  }

  /**
   * Encode all previous commands as Base64 string
   * @return {string} Return the encoded bytes as Base64 string
   */
  encodeAsBase64(): string {
    const uint8Array = this.encode()
    let binaryString = ''
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i])
    }
    return btoa(binaryString)
  }
}
